import React, { useCallback, useEffect, useMemo, useState } from "react"

import moment from "moment-timezone"
import {
    type FieldValues,
    type UseFormSetValue,
    type UseFormWatch,
    useFormContext,
} from "react-hook-form"

import { FeatureFlag } from "@common-shared/config/featureFlags.config"
import { AvailableLookupConsumer } from "@common-shared/config/lookup.config"

import { FRONT_VALIDATION_CONFIG } from "@front-shared/config/validation.config"
import { AutoCompleteElement } from "@front-shared/elements/autoComplete.element"
import { DatePickerElement } from "@front-shared/elements/datePicker.element"
import { InputElement } from "@front-shared/elements/input.element"
import { SelectElement } from "@front-shared/elements/select.element"
import { useCptCodes } from "@front-shared/hooks/cptCodes.hook"

import { COVERAGE_CONFIG } from "../../../../config/coverage.config"
import { CSV_CHECKER_CONFIG } from "../../../../config/csvChecker.config"
import { TOOLTIPS_CONFIG } from "../../../../config/tooltips.config"
import { useCoveragePayerSelection } from "../../../../hooks/coveragePayerSelection.hooks"
import { useIsFeatureFlagEnabled } from "../../../../hooks/isFeatureFlagEnabled.hook"
import { useAppSelector } from "../../../../store/selectors/app.selector"
import { checkerSelector } from "../../../../store/selectors/checker.selector"
import { coveragePayersSelector } from "../../../../store/selectors/coveragePayers.selector"
import { selectedPracticeRoleSelector } from "../../../../store/selectors/selectedPracticeRole.selector"
import type { IDynamicCheckerFormInputData } from "../../../../types/data.types"

export const OptionalFieldsInput: React.FunctionComponent<
    IDynamicFormOptionalFieldsInputProps
> = props => {
    const {
        control,
        formState: { errors },
        setValue,
        register,
        watch,
    } = useFormContext<IDynamicCheckerFormInputData>()

    const dataWatcher = watch()

    const selectedPracticeRole = useAppSelector(selectedPracticeRoleSelector)
    const coveragePayers = useAppSelector(coveragePayersSelector)
    const { manualCheckType } = useAppSelector(checkerSelector)

    const [minNextAppointmentDate] = useState<Date>(new Date())
    const [cptCodeReactKey, setCptCodeReactKey] = useState<number>(
        new Date().getTime(),
    )

    const ehrWriteEligible = useIsFeatureFlagEnabled(
        FeatureFlag.ONE_VERIFY_EHR_WRITE_SINGLE,
    )

    const earlyReleaseFeaturesEligible = useIsFeatureFlagEnabled(
        FeatureFlag.ONE_VERIFY_EARLY_RELEASE_FEATURES,
    )

    const { availableCptCodes } = useCptCodes(
        AvailableLookupConsumer.coveragePortal,
        dataWatcher.modalityCode
            ? [
                  COVERAGE_CONFIG.modalityCodeToModalityMapping[
                      dataWatcher.modalityCode
                  ],
              ]
            : selectedPracticeRole?.availableModalities,
    )

    const { availableCptCodeOptions } = useCoveragePayerSelection(
        coveragePayers,
        availableCptCodes,
        setValue as unknown as UseFormSetValue<FieldValues>,
        watch as unknown as UseFormWatch<FieldValues>,
        true,
    )

    const neededFields = useMemo<(keyof IDynamicCheckerFormInputData)[]>(() => {
        const possibleFields: (keyof IDynamicCheckerFormInputData)[] = [
            "cptCode",
            "customerPatientType",
            "customerPatientId",
            "customerPatientNextAppointmentDate",
            "officeId",
        ]

        return possibleFields.filter(
            field =>
                !CSV_CHECKER_CONFIG.earlyReleaseItems.fields.includes(field) ||
                earlyReleaseFeaturesEligible,
        )
    }, [manualCheckType, earlyReleaseFeaturesEligible])

    // For selects with external value prop we need to set default value
    useEffect(() => {
        props.formDefaultValues?.cptCode &&
            setValue("cptCode", props.formDefaultValues.cptCode)

        props.formDefaultValues?.customerPatientType &&
            setValue(
                "customerPatientType",
                props.formDefaultValues.customerPatientType,
            )
    }, [
        props.formDefaultValues?.cptCode,
        props.formDefaultValues?.customerPatientType,
    ])

    // Reset CPT code react key when modality changes for example
    // And value of cptCode is not in the availableCptCodeOptions
    useEffect(() => {
        if (!dataWatcher.cptCode || !availableCptCodeOptions.length) {
            return
        }

        const isCptCodeAvailable = availableCptCodeOptions.some(
            option => option.value === dataWatcher.cptCode,
        )

        if (!isCptCodeAvailable) {
            setValue("cptCode", undefined, { shouldValidate: true })
            setCptCodeReactKey(new Date().getTime())
        }
    }, [availableCptCodeOptions, dataWatcher.cptCode])

    const onCptCodeSearch = useCallback(
        (searchTerm?: string) => {
            const search = searchTerm?.trim().toLowerCase()

            if (!search) {
                return availableCptCodeOptions
            }

            return availableCptCodeOptions.filter(option => {
                return (
                    option.value.toLowerCase().includes(search) ||
                    option.displayValue?.toLowerCase().includes(search)
                )
            })
        },
        [availableCptCodeOptions],
    )

    const getRenderedFormField = (
        formKey: keyof IDynamicCheckerFormInputData,
        reactKey: string | number,
    ): JSX.Element | null => {
        const nextAppointmentDate =
            dataWatcher.customerPatientNextAppointmentDate ||
            props.formDefaultValues?.customerPatientNextAppointmentDate

        switch (formKey) {
            case "cptCode":
                return (
                    <AutoCompleteElement
                        key={reactKey}
                        label="CPT Code (optional)"
                        placeholder="Select one"
                        name="cptCode"
                        dataFetcher={onCptCodeSearch}
                        itemRenderer={item => item}
                        shouldValidate
                        externalValue={dataWatcher.cptCode}
                        reactHookFormRegister={register as any}
                        reactHookFormSet={setValue as any}
                        reactHookFormErrors={errors}
                        renderedOptions={availableCptCodeOptions}
                        isLabelStatic
                        searchOnFocus
                        ignoreAutoSelectionOnBlur
                    />
                )

            case "customerPatientType":
                return (
                    <SelectElement
                        key={reactKey}
                        label="Patient Type (optional)"
                        placeholder="Select one"
                        name="customerPatientType"
                        allowClear
                        shouldValidate
                        externalValue={
                            dataWatcher.customerPatientType
                                ? COVERAGE_CONFIG.selectRenderedPatientTypes[
                                      dataWatcher.customerPatientType
                                  ].displayValue
                                : undefined
                        }
                        reactHookFormRegister={register as any}
                        reactHookFormSet={setValue as any}
                        reactHookFormErrors={errors}
                        renderedOptions={[
                            ...(dataWatcher.customerPatientType
                                ? [
                                      {
                                          value: undefined,
                                          displayValue: "None",
                                      },
                                  ]
                                : []),
                            ...Object.values(
                                COVERAGE_CONFIG.selectRenderedPatientTypes,
                            ),
                        ]}
                    />
                )

            case "customerPatientId":
                return (
                    <InputElement
                        key={reactKey}
                        label="Patient ID (optional)"
                        tooltip={
                            ehrWriteEligible
                                ? TOOLTIPS_CONFIG.checker.patientId
                                : undefined
                        }
                        name="customerPatientId"
                        reactHookControl={control as any}
                        reactHookValidations={{
                            maxLength: FRONT_VALIDATION_CONFIG.maxLength,
                        }}
                        shouldUnregister={false}
                        reactHookErrors={errors}
                        defaultValue={
                            dataWatcher.customerPatientId ||
                            props.formDefaultValues?.customerPatientId
                        }
                        isLabelStatic
                    />
                )

            case "officeId":
                return (
                    <InputElement
                        key={reactKey}
                        label="Office ID (optional)"
                        name="officeId"
                        reactHookControl={control as any}
                        reactHookValidations={{
                            maxLength: FRONT_VALIDATION_CONFIG.maxLength,
                        }}
                        shouldUnregister={false}
                        reactHookErrors={errors}
                        defaultValue={
                            dataWatcher.officeId ||
                            props.formDefaultValues?.officeId
                        }
                        isLabelStatic
                    />
                )

            case "customerPatientNextAppointmentDate":
                return (
                    <DatePickerElement
                        key={reactKey}
                        name="customerPatientNextAppointmentDate"
                        label="Next Appt Date (optional)"
                        reactHookFormRegister={register as any}
                        reactHookFormSet={setValue as any}
                        reactHookFormErrors={errors}
                        minDate={minNextAppointmentDate}
                        defaultValue={
                            nextAppointmentDate
                                ? moment(nextAppointmentDate)
                                : undefined
                        }
                        isLabelStatic
                        allowClear
                    />
                )

            default:
                return null
        }
    }

    return (
        <div
            className={`
                relative grid grid-flow-row auto-cols-fr gap-x-[24px] gap-y-[40px] grid-cols-2
                ${props.className}
            `}
        >
            {neededFields.map((formKey, index) => {
                return getRenderedFormField(
                    formKey,
                    formKey === "cptCode" ? cptCodeReactKey : index,
                )
            })}
        </div>
    )
}

interface IDynamicFormOptionalFieldsInputProps {
    className?: string
    formDefaultValues?: Partial<IDynamicCheckerFormInputData>
}
